import type { DisciplineBalance, DisciplineBalanceStorePayload } from '../model/interfaces'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

interface DisciplineBalanceStoreApiOptions {
  disciplineId: string | number
  payload: DisciplineBalanceStorePayload
  onStart?: () => void
  onSuccess?: (balance: DisciplineBalance) => void
  onError?: (message: string) => void
  onFinish?: () => void
}

/** `POST /admin-api/disciplines/{discipline}/balances` — a billing write that is NOT idempotent: the
 *  caller keeps the submit disabled while it is pending and never retries it on its own. */
export const useDisciplineBalanceStoreApi = async (
  options: DisciplineBalanceStoreApiOptions,
): Promise<DisciplineBalance | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()

  const url = useAdminRoutes('admin.disciplines.api.balances', { discipline_id: options.disciplineId })

  options.onStart?.()

  try {
    if (!url) {
      const msg = 'Unexpected error. Invalid request URL.'
      toast.error(msg)
      options.onError?.(msg)
      return null
    }

    const { data } = await $axios.post<ApiResponse>(url, options.payload)
    const balance: DisciplineBalance = data.data

    toast.success('Lessons was successfully added!')
    options.onSuccess?.(balance)

    return balance
  } catch (err: unknown) {
    const msg = getAxiosErrorMessage(err)
    toast.error(msg)
    options.onError?.(msg)

    return null
  } finally {
    options.onFinish?.()
  }
}
